import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Chip,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import type { AsnCount, FilterPanelState } from "@/types";
import {
  colors,
  accordionStyles,
  accordionSummaryStyles,
  accordionDetailsStyles,
} from "@/theme/eventsTokens.ts";
import { FilterBadge } from "./FilterWidgets.tsx";

interface AsnPanelProps {
  readonly expanded: boolean;
  readonly onToggle: (_event: React.SyntheticEvent, isExpanded: boolean) => void;
  readonly filters: FilterPanelState;
  readonly onFiltersChange: (filters: FilterPanelState) => void;
  readonly asnOptions?: AsnCount[];
}

export function AsnPanel({ expanded, onToggle, filters, onFiltersChange, asnOptions }: AsnPanelProps) {
  const badge = filters.asns.length;

  const options = (asnOptions ?? []).map((a) => ({
    asn: a.asn,
    count: a.count,
    label: a.as_name ? `${a.as_name} (${a.asn})` : a.asn,
  }));

  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles} data-testid="panel-asn">
        <FilterBadge
          count={badge}
          icon={<TravelExploreIcon sx={{ fontSize: 18, color: colors.textMuted }} />}
          label="Autonomous System"
        />
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <Autocomplete
          multiple
          size="small"
          options={options}
          value={options.filter((a) => filters.asns.includes(a.asn))}
          onChange={(_e, v) =>
            onFiltersChange({ ...filters, asns: v.map((o) => o.asn) })
          }
          getOptionLabel={(opt) =>
            `${opt.label} (${opt.count.toLocaleString()})`
          }
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return opts.filter(
              (o) => o.label.toLowerCase().includes(q) || o.asn.toLowerCase().includes(q),
            );
          }}
          isOptionEqualToValue={(option, value) => option.asn === value.asn}
          renderValue={(value, getItemProps) =>
            value.map((opt, index) => {
              const { key: itemKey, ...itemProps } = getItemProps({ index });
              return (
                <Chip
                  {...itemProps}
                  key={itemKey}
                  label={opt.label}
                  size="small"
                  sx={{ height: 22, fontSize: "0.75rem" }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField {...params} placeholder="Search by name or ASN…" />
          )}
          limitTags={2}
        />
      </AccordionDetails>
    </Accordion>
  );
}
